import { createFileRoute } from '@tanstack/react-router';
import { Clock, Check } from "lucide-react";
import { ContentHeader, ContentSection, FeatureShowcase, FAQSection, CTASection } from "@/components/content";

export const Route = createFileRoute('/use-cases/ai-automation')({
  component: AIAutomationUseCasePage,
  head: () => ({
    meta: [
      { title: 'AI Automation | Matricks' },
      { name: 'description', content: 'Deploy AI agents to automate complex business processes. From customer support to data analysis, let AI handle repetitive work.' },
    ],
  }),
});

const challenges = [
  "Repetitive tasks consuming valuable employee time",
  "24/7 customer support requires large teams",
  "Data analysis backlog growing faster than capacity",
  "Knowledge trapped in documents and systems",
  "Scaling operations requires proportional headcount",
];

const solutions = [
  {
    iconName: "Bot",
    title: "AI Agents",
    description: "Autonomous agents that understand context, make decisions, and take actions.",
  },
  {
    iconName: "Wrench",
    title: "Tool Integration",
    description: "Connect agents to your systems. They can read data, send messages, and update records.",
  },
  {
    iconName: "MessageSquare",
    title: "Natural Language",
    description: "Agents understand plain English. No programming required to define what they do.",
  },
  {
    iconName: "Clock",
    title: "24/7 Operation",
    description: "Agents work around the clock without breaks, handling work as it arrives.",
  },
];

const automationExamples = [
  {
    title: "Customer Support",
    description: "AI agents handle common inquiries, resolve issues, and escalate complex cases to humans.",
    metrics: ["70% ticket deflection", "24/7 availability", "2-second response time"],
  },
  {
    title: "Data Analysis",
    description: "Agents analyze datasets, identify patterns, and generate reports automatically.",
    metrics: ["10x faster analysis", "Consistent methodology", "On-demand insights"],
  },
  {
    title: "Document Processing",
    description: "Extract information from documents, categorize content, and route to appropriate teams.",
    metrics: ["90% accuracy", "Seconds per document", "Handles any format"],
  },
  {
    title: "Research & Summarization",
    description: "Gather information from multiple sources, synthesize findings, and create summaries.",
    metrics: ["Hours to minutes", "Comprehensive coverage", "Structured output"],
  },
];

const faqItems = [
  {
    question: "What tasks can AI agents automate?",
    answer: "AI agents excel at tasks requiring understanding, reasoning, and interaction: customer support, data analysis, document processing, research, content creation, and process orchestration. They're less suited for tasks requiring physical presence or high-stakes decisions without human oversight.",
  },
  {
    question: "How do I ensure agents do the right thing?",
    answer: "Define clear guardrails: what data agents can access, what actions they can take, and when to escalate to humans. Monitor agent behavior with detailed logging and set up alerts for anomalies.",
  },
  {
    question: "What's the ROI of AI automation?",
    answer: "Most organizations see significant time savings within weeks. A customer support agent can handle thousands of inquiries that would otherwise require human agents. ROI depends on your volume and current costs.",
  },
];

function AIAutomationUseCasePage() {
  return (
    <div>
      <ContentHeader
        title="AI Automation"
        description="Put AI to work on your most time-consuming tasks. Matricks AI agents can handle customer support, analyze data, process documents, and automate complex workflows."
        breadcrumbs={[
          { label: "Home", href: "/" },
          { label: "Use Cases", href: "/use-cases" },
          { label: "AI Automation" },
        ]}
        badge="Use Case"
      />

      {/* Challenges */}
      <ContentSection
        id="challenges"
        title="The Challenge"
        description="Scaling operations traditionally means scaling headcount."
      >
        <div className="space-y-4">
          {challenges.map((challenge, index) => (
            <div key={index} className="flex items-start gap-4 p-4 rounded-lg bg-red-500/5 border border-red-500/20">
              <Clock className="h-5 w-5 text-red-500 flex-shrink-0" />
              <span>{challenge}</span>
            </div>
          ))}
        </div>
      </ContentSection>

      {/* Solution */}
      <ContentSection
        id="solution"
        title="The Matricks Solution"
        background="muted"
      >
        <FeatureShowcase features={solutions} columns={2} variant="cards" />
      </ContentSection>

      {/* Examples */}
      <ContentSection
        id="examples"
        title="Automation Examples"
        description="See what's possible with AI agents."
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {automationExamples.map((example) => (
            <div key={example.title} className="rounded-xl border bg-card p-6">
              <h3 className="font-semibold text-lg">{example.title}</h3>
              <p className="text-sm text-muted-foreground mt-2">{example.description}</p>
              <div className="mt-4 flex flex-wrap gap-2">
                {example.metrics.map((metric) => (
                  <span
                    key={metric}
                    className="inline-flex items-center rounded-full bg-green-500/10 px-3 py-1 text-xs text-green-600 dark:text-green-400"
                  >
                    {metric}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
      </ContentSection>

      {/* How It Works */}
      <ContentSection
        id="how-it-works"
        title="Getting Started"
        background="muted"
      >
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          <div className="text-center">
            <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-primary text-primary-foreground text-xl font-bold">
              1
            </div>
            <h3 className="mt-4 font-semibold">Identify Tasks</h3>
            <p className="mt-2 text-sm text-muted-foreground">
              Pick repetitive, time-consuming tasks that follow patterns.
            </p>
          </div>
          <div className="text-center">
            <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-primary text-primary-foreground text-xl font-bold">
              2
            </div>
            <h3 className="mt-4 font-semibold">Create Agent</h3>
            <p className="mt-2 text-sm text-muted-foreground">
              Describe what the agent should do. Connect it to relevant tools.
            </p>
          </div>
          <div className="text-center">
            <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-primary text-primary-foreground text-xl font-bold">
              3
            </div>
            <h3 className="mt-4 font-semibold">Deploy & Monitor</h3>
            <p className="mt-2 text-sm text-muted-foreground">
              Put the agent to work and track performance with analytics.
            </p>
          </div>
        </div>
      </ContentSection>

      {/* Benefits */}
      <ContentSection id="benefits" title="Key Benefits">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {[
            "Scale operations without proportional headcount",
            "24/7 availability for customers and processes",
            "Consistent quality and methodology",
            "Free employees for higher-value work",
            "Faster turnaround on repetitive tasks",
            "Reduced operational costs",
          ].map((benefit) => (
            <div key={benefit} className="flex items-center gap-3">
              <Check className="h-5 w-5 text-green-500 flex-shrink-0" />
              <span>{benefit}</span>
            </div>
          ))}
        </div>
      </ContentSection>

      {/* FAQ */}
      <FAQSection items={faqItems} />

      {/* CTA */}
      <CTASection
        title="Start Automating with AI"
        description="Build your first AI agent in minutes. No coding required."
        primaryAction={{ label: "Start Building", href: "/login" }}
        secondaryAction={{ label: "View Agent Platform", href: "/features/agents-platform" }}
        background="primary"
      />
    </div>
  );
}
